import { Trans, useLingui } from '@lingui/react/macro';
import { useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { useTheme } from '../contexts/ThemeContext';
import { AiProvider, providerColor, toolColor } from '../constants/aiProviders';

/** One tool row of the provider breakdown returned by /api/user/usage/providers. */
export interface ProviderToolStats {
  key: string;
  name: string;
  prompts: number;
  sessions: number;
  tokensIn: number;
  tokensOut: number;
  files: number;
  protectedItems: number;
  activeUsers: number;
  lastActivity: string | null;
  models: { model: string; sessions: number }[];
}

export interface ProviderStats {
  key: string;
  name: string;
  vendor: string;
  totals: ProviderToolStats;
  riskLevels: Record<string, number>;
  tools: ProviderToolStats[];
  daily: {
    date: string;
    prompts: number;
    tokensIn: number;
    tokensOut: number;
    byTool: Record<string, number>;
  }[];
}

interface ProviderDetailModalProps {
  provider: AiProvider;
  stats: ProviderStats | null;
  days: number;
  scope: 'user' | 'company';
  userCount: number;
  onClose: () => void;
}

const compact = (value: number): string => {
  const n = Number(value) || 0;
  if (Math.abs(n) >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (Math.abs(n) >= 10_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
};

const shortDate = (iso: string): string => {
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const monthLabel = (yyyymm: string): string => {
  const d = new Date(`${yyyymm}-01T00:00:00`);
  if (Number.isNaN(d.getTime())) return yyyymm;
  return d.toLocaleDateString(undefined, { month: 'short', year: '2-digit' });
};

// Date only: the time of day adds little in a multi-week report and its width
// is what pushed the tool table into a horizontal scroll.
const formatWhen = (iso: string | null): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

// Reserved status hues (never reused as series colors); labels always ride along.
const RISK_STYLES: Record<string, string> = {
  high: 'bg-red-50 dark:bg-red-500/10 text-red-600 dark:text-red-400 border-red-200 dark:border-red-500/30',
  medium: 'bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-500/30',
  low: 'bg-green-50 dark:bg-green-500/10 text-green-600 dark:text-green-400 border-green-200 dark:border-green-500/30',
};

export default function ProviderDetailModal({
  provider,
  stats,
  days,
  scope,
  userCount,
  onClose,
}: ProviderDetailModalProps) {
  const { isDark } = useTheme();
  const { t } = useLingui();
  const providerName = provider.name;
  const providerVendor = provider.vendor;
  const toolNames = provider.tools.map((tool) => tool.name).join(', ');
  const scopeLabel = scope === 'company' ? t`all ${userCount} team members` : t`your activity`;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  const accent = providerColor(provider, isDark);

  // Tools that saw traffic, in registry order first, unknown ids last.
  const activeTools = useMemo(() => {
    const list = stats?.tools ?? [];
    const order = new Map(provider.tools.map((tool, i) => [tool.key, i]));
    return [...list].sort(
      (a, b) => (order.get(a.key) ?? 99) - (order.get(b.key) ?? 99),
    );
  }, [stats, provider]);

  const chartedTools = activeTools.filter((tool) => tool.prompts > 0 || tool.sessions > 0);

  const toolColors = useMemo(() => {
    const map: Record<string, string> = {};
    activeTools.forEach((tool, i) => {
      map[tool.key] = toolColor(provider, i, isDark);
    });
    return map;
  }, [activeTools, provider, isDark]);

  // Daily series: zero-filled per day for readable ranges, folded to months
  // for the 1-year view so the bar count stays legible.
  const chartData = useMemo(() => {
    const daily = stats?.daily ?? [];
    if (daily.length === 0) return [];

    if (days > 120) {
      const byMonth = new Map<string, Record<string, number | string>>();
      daily.forEach((day) => {
        const month = day.date.slice(0, 7);
        if (!byMonth.has(month)) byMonth.set(month, { label: monthLabel(month) });
        const row = byMonth.get(month)!;
        Object.entries(day.byTool).forEach(([tool, count]) => {
          row[tool] = ((row[tool] as number) || 0) + count;
        });
      });
      return [...byMonth.entries()]
        .sort((a, b) => a[0].localeCompare(b[0]))
        .map(([, row]) => row);
    }

    const byDate = new Map(daily.map((d) => [d.date, d]));
    const first = new Date(`${daily[0].date}T00:00:00`);
    const last = new Date(`${daily[daily.length - 1].date}T00:00:00`);
    const rows: Record<string, number | string>[] = [];
    for (let d = new Date(first); d <= last; d.setDate(d.getDate() + 1)) {
      const iso = d.toISOString().slice(0, 10);
      const day = byDate.get(iso);
      const row: Record<string, number | string> = { label: shortDate(iso) };
      chartedTools.forEach((tool) => {
        row[tool.key] = day?.byTool?.[tool.key] || 0;
      });
      rows.push(row);
    }
    return rows;
  }, [stats, days, chartedTools]);

  const totals = stats?.totals;
  const hasActivity = !!totals && (totals.prompts > 0 || totals.sessions > 0 || totals.files > 0);
  const sharesBase = totals && totals.prompts > 0 ? totals.prompts : 0;

  const topModels = useMemo(() => {
    const tally = new Map<string, number>();
    (stats?.tools ?? []).forEach((tool) => {
      (tool.models ?? []).forEach(({ model, sessions }) => {
        tally.set(model, (tally.get(model) || 0) + sessions);
      });
    });
    return [...tally.entries()].sort((a, b) => b[1] - a[1]).slice(0, 4);
  }, [stats]);

  const riskLabel = (level: 'high' | 'medium' | 'low') =>
    level === 'high' ? t`high risk` : level === 'medium' ? t`medium risk` : t`low risk`;

  const riskEntries = (['high', 'medium', 'low'] as const)
    .map((level) => [level, stats?.riskLevels?.[level] || 0] as const)
    .filter(([, count]) => count > 0);

  const tokensInLabel = compact(totals?.tokensIn || 0);
  const tokensOutLabel = compact(totals?.tokensOut || 0);
  const kpis: { label: string; value: string; hint?: string }[] = [
    { label: t`Prompts protected`, value: compact(totals?.prompts || 0), hint: t`last ${days} days` },
    { label: t`Sessions`, value: compact(totals?.sessions || 0) },
    {
      label: t`Tokens`,
      value: compact((totals?.tokensIn || 0) + (totals?.tokensOut || 0)),
      hint: t`${tokensInLabel} in · ${tokensOutLabel} out`,
    },
    { label: t`Files protected`, value: compact(totals?.files || 0) },
    { label: t`Sensitive items twinned`, value: compact(totals?.protectedItems || 0) },
  ];
  if (scope === 'company') {
    kpis.push({
      label: t`Active users`,
      value: compact(totals?.activeUsers || 0),
      hint: t`of ${userCount} team members`,
    });
  }

  // Rendered through a portal onto <body>: a fixed overlay inside the page
  // tree can end up positioned against a transformed/filtered ancestor
  // (glass-panel blur), which left a gap at the top of the viewport.
  return createPortal(
    <div
      className="fixed inset-0 z-50 bg-black/50 flex p-3 sm:p-6 overflow-y-auto"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={t`${providerName} activity report`}
    >
      {/* m-auto centers when the modal fits and top-anchors when it is taller
          than the viewport (items-center would clip the header off-screen). */}
      <div
        className="m-auto w-full max-w-4xl rounded-2xl shadow-2xl border overflow-hidden"
        style={{ backgroundColor: 'var(--color-surface)', borderColor: 'var(--color-border)' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between gap-3 px-5 sm:px-6 py-4 border-b sticky top-0 z-10"
          style={{ backgroundColor: 'var(--color-surface)', borderColor: 'var(--color-border)' }}
        >
          <div className="flex items-center gap-3 min-w-0">
            <div
              className={`w-10 h-10 rounded-xl ${provider.bgLight} flex items-center justify-center flex-shrink-0`}
            >
              <span className="w-3.5 h-3.5 rounded-full" style={{ backgroundColor: accent }} />
            </div>
            <div className="min-w-0">
              <h2 className="text-base font-bold truncate" style={{ color: 'var(--color-text)' }}>
                <Trans>{providerName} activity report</Trans>
              </h2>
              <p className="text-xs truncate" style={{ color: 'var(--color-text-muted)' }}>
                <Trans>{providerVendor} · last {days} days · {scopeLabel}</Trans>
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg transition-colors hover:bg-slate-100 dark:hover:bg-white/10 flex-shrink-0"
            style={{ color: 'var(--color-text-muted)' }}
            aria-label={t`Close report`}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-5 sm:p-6 space-y-6">
          {!hasActivity ? (
            <div className="py-14 text-center space-y-2">
              <p className="text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>
                <Trans>No {providerName} activity in the last {days} days</Trans>
              </p>
              <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                <Trans>Prompts sent through {toolNames} will appear here once protected.</Trans>
              </p>
            </div>
          ) : (
            <>
              {/* KPI tiles */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {kpis.map((kpi) => (
                  <div
                    key={kpi.label}
                    className="rounded-xl border p-3.5"
                    style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-surface-secondary)' }}
                  >
                    <p className="text-[11px]" style={{ color: 'var(--color-text-muted)' }}>{kpi.label}</p>
                    <p className="text-xl font-semibold mt-0.5" style={{ color: 'var(--color-text)' }}>{kpi.value}</p>
                    {kpi.hint && (
                      <p className="text-[10px] mt-0.5" style={{ color: 'var(--color-text-muted)' }}>{kpi.hint}</p>
                    )}
                  </div>
                ))}
              </div>

              {/* Daily activity by tool */}
              {chartData.length > 0 && chartedTools.length > 0 && (
                <section>
                  <h3 className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: 'var(--color-text-muted)' }}>
                    {days > 120 ? <Trans>Monthly prompts by tool</Trans> : <Trans>Daily prompts by tool</Trans>}
                  </h3>
                  <div className="h-56">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={chartData} barCategoryGap="25%">
                        <CartesianGrid stroke="var(--chart-grid)" strokeWidth={1} vertical={false} />
                        <XAxis
                          dataKey="label"
                          stroke="var(--chart-axis)"
                          tick={{ fill: 'var(--chart-axis)', fontSize: 11 }}
                          tickLine={false}
                          interval="preserveStartEnd"
                          minTickGap={24}
                        />
                        <YAxis
                          allowDecimals={false}
                          stroke="var(--chart-axis)"
                          tick={{ fill: 'var(--chart-axis)', fontSize: 11 }}
                          tickLine={false}
                          axisLine={false}
                          width={36}
                        />
                        <Tooltip
                          cursor={{ fill: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(15,23,42,0.04)' }}
                          contentStyle={{
                            background: 'var(--tooltip-bg)',
                            border: '1px solid var(--tooltip-border)',
                            borderRadius: '0.5rem',
                            color: 'var(--color-text)',
                            fontSize: '0.75rem',
                          }}
                          labelStyle={{ color: 'var(--color-text)', fontWeight: 600 }}
                        />
                        {chartedTools.length > 1 && (
                          <Legend wrapperStyle={{ fontSize: '0.72rem', paddingTop: '0.5rem' }} iconType="circle" iconSize={8} />
                        )}
                        {chartedTools.map((tool, i) => (
                          <Bar
                            key={tool.key}
                            dataKey={tool.key}
                            name={tool.name}
                            stackId="tools"
                            fill={toolColors[tool.key]}
                            stroke="var(--color-surface)"
                            strokeWidth={2}
                            maxBarSize={24}
                            radius={i === chartedTools.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]}
                          />
                        ))}
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </section>
              )}

              {/* Tool share (part-to-whole) */}
              {sharesBase > 0 && chartedTools.length > 1 && (
                <section>
                  <h3 className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: 'var(--color-text-muted)' }}>
                    <Trans>Prompt share by tool</Trans>
                  </h3>
                  <div className="flex h-4 rounded-full overflow-hidden gap-0.5" role="img" aria-label={t`Prompt share by tool`}>
                    {chartedTools.map((tool) => {
                      const pct = (tool.prompts / sharesBase) * 100;
                      if (pct <= 0) return null;
                      return (
                        <div
                          key={tool.key}
                          title={`${tool.name}: ${Math.round(pct)}%`}
                          style={{ width: `${pct}%`, backgroundColor: toolColors[tool.key] }}
                        />
                      );
                    })}
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
                    {chartedTools.map((tool) => (
                      <span key={tool.key} className="inline-flex items-center gap-1.5 text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: toolColors[tool.key] }} />
                        {tool.name}
                        <span style={{ color: 'var(--color-text-muted)' }}>
                          {sharesBase > 0 ? Math.round((tool.prompts / sharesBase) * 100) : 0}%
                        </span>
                      </span>
                    ))}
                  </div>
                </section>
              )}

              {/* Per-tool statistics table */}
              <section>
                <h3 className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: 'var(--color-text-muted)' }}>
                  <Trans>Tool statistics</Trans>
                </h3>
                {/* Column set + date-only formatting are sized to fit the modal
                    without a horizontal scrollbar (tokens in/out share a column). */}
                <div className="rounded-xl border overflow-hidden" style={{ borderColor: 'var(--color-border)' }}>
                  <table className="w-full text-sm">
                    <thead style={{ backgroundColor: 'var(--color-surface-secondary)' }}>
                      <tr className="text-[10px] font-bold uppercase tracking-widest" style={{ color: 'var(--color-text-muted)' }}>
                        <th className="text-left px-3 py-2.5"><Trans>Tool</Trans></th>
                        <th className="text-right px-3 py-2.5"><Trans>Prompts</Trans></th>
                        <th className="text-right px-3 py-2.5"><Trans>Sessions</Trans></th>
                        <th className="text-right px-3 py-2.5"><Trans>Tokens</Trans></th>
                        <th className="text-right px-3 py-2.5"><Trans>Files</Trans></th>
                        <th className="text-right px-3 py-2.5"><Trans>Sensitive items</Trans></th>
                        {scope === 'company' && <th className="text-right px-3 py-2.5"><Trans>Users</Trans></th>}
                        <th className="text-right px-3 py-2.5"><Trans>Last active</Trans></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y" style={{ borderColor: 'var(--color-border)' }}>
                      {activeTools.map((tool) => {
                        const tokensInLabel = compact(tool.tokensIn);
                        const tokensOutLabel = compact(tool.tokensOut);
                        return (
                        <tr key={tool.key} className="hover:bg-slate-50 dark:hover:bg-white/[0.03]">
                          <td className="px-3 py-2.5 font-medium" style={{ color: 'var(--color-text)' }}>
                            <span className="inline-flex items-center gap-2">
                              <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: toolColors[tool.key] }} />
                              {tool.name}
                            </span>
                          </td>
                          <td className="px-3 py-2.5 text-right tabular-nums" style={{ color: 'var(--color-text-secondary)' }}>{tool.prompts.toLocaleString()}</td>
                          <td className="px-3 py-2.5 text-right tabular-nums" style={{ color: 'var(--color-text-secondary)' }}>{tool.sessions.toLocaleString()}</td>
                          <td className="px-3 py-2.5 text-right whitespace-nowrap">
                            <span className="tabular-nums" style={{ color: 'var(--color-text-secondary)' }}>
                              {(tool.tokensIn + tool.tokensOut).toLocaleString()}
                            </span>
                            <span className="block text-[10px] tabular-nums" style={{ color: 'var(--color-text-muted)' }}>
                              <Trans>{tokensInLabel} in · {tokensOutLabel} out</Trans>
                            </span>
                          </td>
                          <td className="px-3 py-2.5 text-right tabular-nums" style={{ color: 'var(--color-text-secondary)' }}>{tool.files.toLocaleString()}</td>
                          <td className="px-3 py-2.5 text-right tabular-nums" style={{ color: 'var(--color-text-secondary)' }}>{tool.protectedItems.toLocaleString()}</td>
                          {scope === 'company' && (
                            <td className="px-3 py-2.5 text-right tabular-nums" style={{ color: 'var(--color-text-secondary)' }}>{tool.activeUsers.toLocaleString()}</td>
                          )}
                          <td className="px-3 py-2.5 text-right whitespace-nowrap" style={{ color: 'var(--color-text-muted)' }}>{formatWhen(tool.lastActivity)}</td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </section>

              {/* Risk + models footnotes */}
              {(riskEntries.length > 0 || topModels.length > 0) && (
                <section className="flex flex-col sm:flex-row gap-4">
                  {riskEntries.length > 0 && (
                    <div className="flex-1">
                      <h3 className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: 'var(--color-text-muted)' }}>
                        <Trans>Session risk levels</Trans>
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {riskEntries.map(([level, count]) => (
                          <span
                            key={level}
                            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-medium capitalize ${RISK_STYLES[level]}`}
                          >
                            {riskLabel(level)}
                            <span className="tabular-nums">{count.toLocaleString()}</span>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {topModels.length > 0 && (
                    <div className="flex-1">
                      <h3 className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: 'var(--color-text-muted)' }}>
                        <Trans>Top models</Trans>
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {topModels.map(([model, sessions]) => (
                          <span
                            key={model}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-mono"
                            style={{ borderColor: 'var(--color-border)', color: 'var(--color-text-secondary)' }}
                          >
                            {model}
                            <span className="tabular-nums" style={{ color: 'var(--color-text-muted)' }}>{sessions}</span>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </section>
              )}

              <p className="text-[11px] pt-1 border-t" style={{ color: 'var(--color-text-muted)', borderColor: 'var(--color-border)' }}>
                <Trans>Aggregate statistics only. Individual conversations are available to administrators in User Management.</Trans>
              </p>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
